import React, { useRef, useEffect } from 'react';
import classNames from './TopicsSlider.module.scss';
import classes from 'classnames';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/store';
import { gameActions, gameSelector } from '@/store/reducers/game';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation } from 'swiper';
import commonUtils from '@/utils/common';
import UseTopicQuizzes from '@/hooks/UseTopicQuizzes';
import { Quiz } from '@/types/quiz';
import lock from '@/assets/svg/lock.svg';
import Skeleton from 'react-loading-skeleton';
import QuizBadge from '@/components/QuizBadge/QuizBadge';
import QuizScore from '@/components/QuizScore/QuizScore';
import { useNavigate } from 'react-router-dom';
import UseLocalLang from '@/hooks/UseLocalLang';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import TheButton from '@/components/TheButton/TheButton';
import AppLoaderCircle from '@/components/AppLoaderCircle';
import { useDrawer } from '@/context/DrawerContext';
import UseUnitsAssets from '@/hooks/UseUnitsAssets';
import { UnitAssets } from '@/types/units';
import { unitSelector } from '@/store/reducers/unit';
import { authSelector } from '@/store/reducers/auth';
import { activityLocationsActions } from '@/store/reducers/activityLocations';
import UserUtils from '@/utils/user';
import UseScreenSize from '@/hooks/UseScreenSize';
import AccessibleButton from '@/components/AccessibleButton/AccessibleButton';
import UseFocusTrap from '@/hooks/UseFocusTrap';

export const TopicsSlider = () => {
  const { t } = useTranslation();
  const { isMobileSmall } = UseScreenSize();
  const drawer = useDrawer();
  const { unitsAssets } = UseUnitsAssets();
  const {
    selectedUnit,
    selectedLevel,
    selectedTopic: selectedUnitsTopic,
  } = useAppSelector(unitSelector);
  const { user } = useAppSelector(authSelector);
  const unitAssets: UnitAssets = unitsAssets[selectedUnit?.order || 1];
  const { quizzesGroups, loadingQuizzes, quizzesPerPage } = UseTopicQuizzes();
  const { selectedTopic } = useAppSelector(gameSelector);
  const isTeacher = UserUtils.isTeacher(user);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const localLang = UseLocalLang();
  const [activeSlide, setActiveSlide] = React.useState(0);
  const modalRef = useRef<HTMLDivElement>(null);
  const firstQuizRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (firstQuizRef.current) {
      firstQuizRef.current.focus({ preventScroll: true });
    }
  }, [activeSlide, loadingQuizzes]);

  const handleKeyDown = UseFocusTrap(modalRef);

  const getQuizzesGroup = () => {
    if (loadingQuizzes) {
      const quizzes = commonUtils.groupBySize(
        Array(selectedTopic?.quizzesCount ?? 0).fill({}),
        quizzesPerPage
      );

      return quizzes;
    }

    return quizzesGroups;
  };

  const goToQuizPage = async (quiz: Quiz) => {
    dispatch(gameActions.setSelectedQuiz(quiz));
    if (selectedUnitsTopic && selectedUnit) {
      dispatch(
        activityLocationsActions.setActivityLocation({
          activityId: quiz.id,
          topic: selectedUnitsTopic,
          activitiesInTopic: selectedTopic?.activitiesInTopic,
          unit: selectedUnit,
          level: selectedLevel,
        })
      );
    }
    navigate('/dashboard/quiz');
    drawer.closeTopicsDrawer();
  };

  const quizesGroup = getQuizzesGroup();
  const selectedTopicId = selectedTopic?.id;
  const lockedOrder = commonUtils.getLockedOrder(quizesGroup, selectedTopicId, user);
  const quizzesGroupsElements = quizesGroup.map((quizzesGroup, groupIndex) => {
    const quizzesElements = quizzesGroup.map((quiz: Quiz, quizIndex: number) => {
      const index = groupIndex * quizzesPerPage + quizIndex;
      const isOrderFirst = quiz.order === 1;
      const isBeyondLockedOrder = quiz.order > lockedOrder + 1;
      const isLocked = !isTeacher && !isOrderFirst && isBeyondLockedOrder;
      const isCompleted = commonUtils.isCompleted(
        quiz.id,
        selectedTopicId,
        user?.progress?.topic?.activitiesOverviewPerTopic
      );
      const isSkipped = commonUtils.isSkipped(
        quiz.id,
        selectedTopicId,
        user?.progress?.topic?.activitiesOverviewPerTopic
      );

      const achievedStars = (!isLocked && quiz.userData?.cumulativeAchievedStars) || 0;

      let badgeImgSrc = lock;

      if (isSkipped) {
        badgeImgSrc = unitAssets?.skipped;
      } else if (!isLocked) {
        badgeImgSrc = unitAssets?.medal;
      }

      return (
        <AccessibleButton
          key={quizIndex}
          ref={groupIndex === activeSlide && quizIndex === 0 ? firstQuizRef : undefined}
          className={classes(
            classNames.quiz,
            {
              [classNames.quizLocked]: isLocked,
            },
            !isLocked ? 'cursor-pointer' : ''
          )}
          onClick={() => !isLocked && goToQuizPage(quiz)}
          disabled={isLocked || loadingQuizzes}
          tabIndex={groupIndex === activeSlide && !isLocked ? 0 : -1}
          role='button'
        >
          {loadingQuizzes ? (
            <Skeleton width='45px' height='45px' circle />
          ) : (
            <QuizBadge
              img={badgeImgSrc}
              isLocked={isLocked}
              isSkipped={isSkipped}
              index={index}
              isSmall={true}
              source={isMobileSmall ? '' : 'topicsDrawer'}
            />
          )}
          {loadingQuizzes ? (
            <Skeleton width='70px' style={{ position: 'relative', top: '-10px' }} />
          ) : (
            <>
              <div style={{ marginTop: isMobileSmall ? 0 : 5 }}>
                <QuizScore
                  isLocked={isLocked}
                  achievedStars={achievedStars}
                  source='topicsDrawer'
                />
              </div>
              <span className={classNames.name}>{commonUtils.getName(quiz, localLang)}</span>
            </>
          )}
        </AccessibleButton>
      );
    });

    return (
      <SwiperSlide key={groupIndex} className={classNames.quizzesGrid}>
        {quizzesElements}
      </SwiperSlide>
    );
  });

  const [activeIndex, setActiveIndex] = React.useState(0);
  const swiperRef = React.useRef<any>(null);

  // Reset to first page when topic changes
  React.useEffect(() => {
    setActiveIndex(0);
    if (swiperRef.current) {
      swiperRef.current.slideTo(0);
    }
  }, [selectedTopic?.id]);

  const showNavigation = quizzesGroupsElements.length > 1;

  return (
    <div
      className={classes(classNames.topicsSlider, {
        [classNames.topicsSliderMobile]: isMobileSmall,
      })}
      style={{
        backgroundImage: `url(${isMobileSmall ? unitAssets?.topicsBackgroundImageMobile : unitAssets?.topicsBackgroundImage})`,
      }}
    >
      <div
        className={classes(classNames.modal, 'activities-modal')}
        ref={modalRef}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        <div
          className={classNames.modalContentInner}
          style={
            {
              '--modalBorderColor': unitAssets.modalBorderColor,
            } as React.CSSProperties
          }
        >
          {loadingQuizzes ? (
            <>
              <div className={classNames.loading}>
                <AppLoaderCircle />
              </div>
            </>
          ) : selectedTopic?.activitiesInTopic && selectedTopic?.activitiesInTopic > 0 ? (
            <>
              <div className={classNames.modalBody}>
                {!isMobileSmall && showNavigation && (
                  <AccessibleButton
                    className={classes(
                      classNames.swiperButton,
                      'swiper-button image-swiper-button-prev'
                    )}
                    style={{ cursor: 'pointer' }}
                    ariaLabel='Previous'
                    onClick={() => {}}
                  >
                    <IoIosArrowBack />
                  </AccessibleButton>
                )}
                {!isMobileSmall && showNavigation && (
                  <AccessibleButton
                    className={classes(
                      classNames.swiperButton,
                      'swiper-button image-swiper-button-next'
                    )}
                    ariaLabel='Next'
                    style={{ cursor: 'pointer' }}
                    onClick={() => {}}
                  >
                    <IoIosArrowForward />
                  </AccessibleButton>
                )}
                <Swiper
                  modules={isMobileSmall ? [Navigation, Pagination] : [Navigation]}
                  className={classNames.quizzesSwiper}
                  spaceBetween={10}
                  slidesPerView={1}
                  navigation={
                    showNavigation
                      ? {
                          nextEl: '.image-swiper-button-next',
                          prevEl: '.image-swiper-button-prev',
                          disabledClass: 'swiper-button-disabled',
                        }
                      : false
                  }
                  draggable={true}
                  injectStyles={[
                    `
                      .swiper-wrapper{
                        align-items: center;
                      }
                      `,
                  ]}
                  pagination={{
                    clickable: true,
                    horizontalClass: 'swiperPagination',
                    bulletClass: 'swiperPaginationBullet',
                    bulletActiveClass: 'swiperPaginationBulletActive',
                  }}
                  onSwiper={swiper => {
                    swiperRef.current = swiper;
                    setActiveSlide(0);
                  }}
                  onSlideChange={swiper => setActiveSlide(swiper.activeIndex)}
                >
                  {quizzesGroupsElements}
                </Swiper>
              </div>
              {!isMobileSmall && (
                <TheButton
                  text='Back'
                  className={classNames.backBtn}
                  emitOnClick={drawer.closeTopicsDrawer}
                  shouldRecolor
                />
              )}
            </>
          ) : (
            <>
              <div className={classNames.emptyTopic}>
                <div className={classNames.emptyTopicName}>{selectedTopic?.name}VVVV</div>
                <div
                  className={classNames.emptyTopicText}
                  dangerouslySetInnerHTML={{ __html: t('comingSoon').toString() }}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
